import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateTime, londonDateTimeInputInstant } from "../dates";
import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router";
import { api } from "../api";
import { ErrorState, Loading } from "../ui/State";
export function ResidentPollsAlpha({ premises }: { premises: string }) {
  const root = "/api/portal/leaseholder/" + premises + "/polls",
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ id: string; body: any } | null>(null);
  const load = () => api(root).then(setModel);
  useEffect(() => {
    void load().catch(setError);
  }, [root]);
  async function send(id: string, body: any) {
    pending.current ??= {
      id,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api(root + "/" + pending.current.id, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel">
      <h2>Your views</h2>
      <p>
        One response per property. These consultations do not replace formal
        company voting.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void send("", {})}>
          Retry the same response
        </button>
      )}
      {model?.polls.length === 0 && <p>No open consultations.</p>}
      {model?.polls.map((p: any) => (
        <article key={p.id}>
          <h3>{p.question}</h3>
          <p>Closes {formatDateTime(p.closes_at)}</p>
          {p.responded ? (
            <p>
              Your property has responded
              {p.choice !== null ? ": " + p.options[p.choice] : "."}
            </p>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void send(p.id, {
                  choice: Number(f.get("choice")),
                  feedback: f.get("feedback"),
                });
              }}
            >
              <fieldset disabled={busy || !!pending.current}>
                <legend>Your response</legend>
                {p.options.map((o: string, i: number) => (
                  <label key={i}>
                    <input type="radio" name="choice" value={i} required />
                    {o}
                  </label>
                ))}
                <label>
                  Additional feedback
                  <textarea name="feedback" maxLength={2000} />
                </label>
                <p>
                  Your manager can read your feedback. Avoid including sensitive
                  personal information.
                </p>
                <button>Submit response</button>
              </fieldset>
            </form>
          )}
        </article>
      ))}
    </section>
  );
}
export function PollsAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ url: string; body: any } | null>(null);
  const load = () =>
    building
      ? api("/api/alpha/polls?building=" + building).then(setModel)
      : Promise.resolve();
  useEffect(() => {
    setModel(undefined);
    void load().catch(setError);
  }, [building]);
  async function save(url: string, body: any) {
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Resident polls and feedback</h1>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Choose a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        <>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save("/api/alpha/polls", {
                building,
                question: f.get("question"),
                options: String(f.get("options"))
                  .split("\n")
                  .map((s) => s.trim())
                  .filter(Boolean),
                closesAt: londonDateTimeInputInstant(
                  String(f.get("closes")) + "T23:59",
                )!,
              });
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Prepare consultation</legend>
              <label>
                Question
                <input name="question" required minLength={5} maxLength={500} />
              </label>
              <label>
                Options — one per line, two to eight
                <textarea name="options" required />
              </label>
              <label>
                Closing date (closes at 23:59)
                <DateOnlyInput name="closes" required />
              </label>
              <button>Prepare poll</button>
            </fieldset>
          </form>
          {!model.polls.length&&<p className="empty">No polls have been prepared for this building.</p>}
          {model.polls.map((p: any) => (
            <section className="panel" key={p.id}>
              <h2>{p.question}</h2>
              <p>
                {p.status} · {p.responses.length} properties responded
              </p>
              <ul>
                {p.options.map((o: string, i: number) => (
                  <li key={i}>
                    {o}: {p.responses.filter((r: any) => r.choice === i).length}
                  </li>
                ))}
              </ul>
              {p.responses
                .filter((r: any) => r.feedback)
                .map((r: any, i: number) => (
                  <p key={i}>{r.feedback}</p>
                ))}
              {p.status === "Draft" && (
                <button
                  disabled={busy || !!pending.current}
                  onClick={() =>
                    void save("/api/alpha/polls/" + p.id + "/publish", {})
                  }
                >
                  Publish to residents
                </button>
              )}
              {p.status === "Published" && (
                <button
                  disabled={busy || !!pending.current}
                  onClick={() =>
                    void save("/api/alpha/polls/" + p.id + "/close", {})
                  }
                >
                  Close consultation
                </button>
              )}
              {p.action_id ? (
                <Link to={"/work/actions/" + p.action_id}>Open follow-up</Link>
              ) : (
                p.status === "Closed" && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      void save("/api/alpha/polls/" + p.id + "/follow-up", {
                        note: f.get("note"),
                        dueDate: f.get("due"),
                      });
                    }}
                  >
                    <fieldset disabled={busy || !!pending.current}>
                      <legend>Act on feedback</legend>
                      <label>
                        Decision and next steps
                        <textarea name="note" required minLength={5} />
                      </label>
                      <label>
                        Due date
                        <DateOnlyInput name="due" required />
                      </label>
                      <button>Create follow-up</button>
                    </fieldset>
                  </form>
                )
              )}
            </section>
          ))}
        </>
      )}
    </>
  );
}
